import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import Card from 'components/Card';
import DateTimePicker from 'components/DateTimePicker';
import { Input, Textarea } from 'components/FormFields';
import SearchableSelect from 'components/FormFields/SearchableSelect';
import { useGetSeriesAdmin } from 'components/SeriesSelect/useGetSeriesAdmin';
import { useTrackSearch } from 'pages/app/Events/CreateEventModal/hooks/useTrackSearch';
import {
  Container,
  CardHeader,
  CardIcon,
  CardTitleGroup,
  CardTitle,
  CardSubtitle,
  SectionTitle,
  SectionDescription,
  Divider,
  FormRow,
  FieldWrapper,
  FieldLabel,
  ToggleRow,
  ToggleInfo,
  ToggleIcon,
  ToggleText,
  ToggleLabel,
  ToggleDescription,
  Toggle,
  ToggleInput,
  ToggleSlider,
  CollapsibleHeader,
  CollapsibleIcon,
  CollapsibleContent,
  LoadingContainer,
  EmptyState,
  SeriesTagsContainer,
  SeriesTag,
  RemoveTagButton,
  ExpandedSection,
  InfoBox,
  InfoText,
  SectionLabel,
  OptionalBadge,
} from './StyledComponents';

const GET_SEATING_CHARTS = gql`
  query GetSeatingCharts {
    getSeatingCharts {
      id
      name
    }
  }
`;

// Event type options for multi-select
const EVENT_TYPE_OPTIONS = [
  { value: 'Weekly', label: 'Weekly' },
  { value: 'Thrill_Show', label: 'Thrill Show' },
  { value: 'Special_Show', label: 'Special Show' },
  { value: 'Touring_Series', label: 'Touring Series' },
  { value: 'Street_Drags', label: 'Street Drags' },
  { value: 'Drifting', label: 'Drifting' },
  { value: 'Competition_Drags', label: 'Competition Drags' },
  { value: 'Monster_Truck_Show', label: 'Monster Truck Show' },
  { value: 'Enduo', label: 'Enduo' },
  { value: 'Drag_Racing', label: 'Drag Racing' },
  { value: 'Autocross', label: 'Autocross' },
];

// Icons
const InfoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const SettingsIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const XIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const LayersIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 2 7 12 12 22 7 12 2" />
    <polyline points="2 17 12 22 22 17" />
    <polyline points="2 12 12 17 22 12" />
  </svg>
);

const TicketIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
    <path d="M13 5v2" />
    <path d="M13 17v2" />
    <path d="M13 11v2" />
  </svg>
);

const ReservedSeatIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v5m-4 0h4" />
  </svg>
);

const SalesWindowIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z" />
  </svg>
);

const DetailsTab = ({
  settings = {},
  onSettingsChange,
  divisions = [],
  loading,
}) => {
  const [adminSettingsOpen, setAdminSettingsOpen] = useState(false);

  // Fetch series options
  const { data: seriesData, loading: seriesLoading } = useGetSeriesAdmin();
  const seriesOptions =
    seriesData?.getSeriesAdmin?.results?.map((s) => ({
      value: s.id,
      label: s.name,
    })) || [];

  // Fetch track options
  const { tracks, loading: tracksLoading } = useTrackSearch();

  // Fetch seating charts (only displayed when reserved seating is enabled)
  const { data: chartsData, loading: chartsLoading } =
    useQuery(GET_SEATING_CHARTS);
  const seatingChartOptions = (chartsData?.getSeatingCharts || []).map(
    (chart) => ({
      value: chart.id,
      label: chart.name,
    })
  );

  // Handle field changes
  const handleChange = useCallback(
    (field) => (e) => {
      const value = e?.target?.value ?? e;
      onSettingsChange({
        ...settings,
        [field]: value,
      });
    },
    [settings, onSettingsChange]
  );

  // Handle toggle changes
  const handleToggleChange = useCallback(
    (field) => (e) => {
      const next = {
        ...settings,
        [field]: e.target.checked,
      };
      // Clear the selected seating chart when reserved seating is disabled
      if (field === 'reserved_seats' && !e.target.checked) {
        next.seating_chart_id = null;
      }
      onSettingsChange(next);
    },
    [settings, onSettingsChange]
  );

  // Handle seating chart change
  const handleSeatingChartChange = useCallback(
    (option) => {
      onSettingsChange({
        ...settings,
        seating_chart_id: option?.value || null,
      });
    },
    [settings, onSettingsChange]
  );

  // Handle division add
  const handleDivisionAdd = useCallback(
    (option) => {
      if (!option?.value) return;
      const currentIds = settings.division_ids || [];
      if (!currentIds.includes(option.value)) {
        onSettingsChange({
          ...settings,
          division_ids: [...currentIds, option.value],
        });
      }
    },
    [settings, onSettingsChange]
  );

  // Handle division removal
  const handleDivisionRemove = useCallback(
    (divisionId) => {
      const currentIds = settings.division_ids || [];
      onSettingsChange({
        ...settings,
        division_ids: currentIds.filter((id) => id !== divisionId),
      });
    },
    [settings, onSettingsChange]
  );

  // Handle event type add
  const handleEventTypeAdd = useCallback(
    (option) => {
      if (!option?.value) return;
      const currentTypes = settings.event_types || [];
      if (!currentTypes.includes(option.value)) {
        onSettingsChange({
          ...settings,
          event_types: [...currentTypes, option.value],
        });
      }
    },
    [settings, onSettingsChange]
  );

  // Handle event type removal
  const handleEventTypeRemove = useCallback(
    (eventType) => {
      const currentTypes = settings.event_types || [];
      onSettingsChange({
        ...settings,
        event_types: currentTypes.filter((t) => t !== eventType),
      });
    },
    [settings, onSettingsChange]
  );

  // Handle series selection
  const handleSeriesAdd = useCallback(
    (option) => {
      if (!option?.value) return;
      const currentIds = settings.series_ids || [];
      if (!currentIds.includes(option.value)) {
        onSettingsChange({
          ...settings,
          series_ids: [...currentIds, option.value],
        });
      }
    },
    [settings, onSettingsChange]
  );

  // Handle series removal
  const handleSeriesRemove = useCallback(
    (seriesId) => {
      const currentIds = settings.series_ids || [];
      onSettingsChange({
        ...settings,
        series_ids: currentIds.filter((id) => id !== seriesId),
      });
    },
    [settings, onSettingsChange]
  );

  // Handle code expiration change (combines date and time into two fields)
  const handleCodeExpirationChange = useCallback(
    (dateValue) => {
      if (!dateValue) {
        onSettingsChange({
          ...settings,
          code_date: '',
          code_time: '',
        });
        return;
      }

      // dateValue is a Date object from DateTimePicker
      const date = dateValue instanceof Date ? dateValue : new Date(dateValue);

      // Format date as MM-DD-YYYY
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      const formattedDate = `${month}-${day}-${year}`;

      // Format time as HH:MM AM/PM
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const formattedTime = `${displayHours}:${minutes
        .toString()
        .padStart(2, '0')} ${ampm}`;

      onSettingsChange({
        ...settings,
        code_date: formattedDate,
        code_time: formattedTime,
      });
    },
    [settings, onSettingsChange]
  );

  // Combine code_date and code_time into a Date object for the picker
  const getCodeExpirationValue = useCallback(() => {
    if (!settings.code_date) return null;

    // Parse MM-DD-YYYY format
    const dateParts = settings.code_date.split('-');
    if (dateParts.length !== 3) return null;

    const month = parseInt(dateParts[0]) - 1;
    const day = parseInt(dateParts[1]);
    const year = parseInt(dateParts[2]);

    // Parse time if available
    let hours = 12;
    let minutes = 0;
    if (settings.code_time) {
      const timeMatch = settings.code_time.match(
        /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i
      );
      if (timeMatch) {
        hours = parseInt(timeMatch[1]);
        minutes = parseInt(timeMatch[2]);
        const period = timeMatch[3].toUpperCase();
        if (period === 'PM' && hours !== 12) hours += 12;
        if (period === 'AM' && hours === 12) hours = 0;
      }
    }

    return new Date(year, month, day, hours, minutes, 0, 0);
  }, [settings.code_date, settings.code_time]);

  // Get series name by ID
  const getSeriesName = (seriesId) => {
    const series = seriesOptions.find((s) => s.value === seriesId);
    return series?.label || `Series ${seriesId}`;
  };

  // Get division name by ID
  const getDivisionName = (divisionId) => {
    const division = divisions.find((d) => d.id === divisionId);
    return division?.name || `Division ${divisionId}`;
  };

  // Get event type label by value
  const getEventTypeLabel = (eventTypeValue) => {
    const eventType = EVENT_TYPE_OPTIONS.find(
      (t) => t.value === eventTypeValue
    );
    return eventType?.label || eventTypeValue;
  };

  // Convert divisions to options for SearchableSelect
  const divisionOptions = divisions.map((d) => ({
    value: d.id,
    label: d.name,
  }));

  if (loading) {
    return (
      <Card>
        <LoadingContainer>Loading event details...</LoadingContainer>
      </Card>
    );
  }

  return (
    <Container>
      {/* Basic Information Card */}
      <Card>
        <CardHeader>
          <CardIcon>
            <InfoIcon />
          </CardIcon>
          <CardTitleGroup>
            <CardTitle>Basic Information</CardTitle>
            <CardSubtitle>Core details about your event</CardSubtitle>
          </CardTitleGroup>
        </CardHeader>

        <FieldWrapper>
          <Input
            label="Event Name"
            required
            value={settings.name || ''}
            onChange={handleChange('name')}
            placeholder="Enter event name"
          />
        </FieldWrapper>

        <FormRow columns="1fr 1fr">
          <FieldWrapper noMargin>
            <SearchableSelect
              label="Track"
              required
              value={settings.track_id}
              onChange={(option) => {
                onSettingsChange({
                  ...settings,
                  track_id: option?.value || null,
                });
              }}
              options={tracks}
              loading={tracksLoading}
              placeholder="Search tracks..."
            />
          </FieldWrapper>
          <div>
            <SearchableSelect
              key={`series-select-${(settings.series_ids || []).length}`}
              label="Series"
              placeholder="Search and add series..."
              options={seriesOptions.filter(
                (s) => !(settings.series_ids || []).includes(s.value)
              )}
              loading={seriesLoading}
              onChange={handleSeriesAdd}
              value={null}
            />
            {(settings.series_ids || []).length > 0 && (
              <SeriesTagsContainer>
                {(settings.series_ids || []).map((seriesId) => (
                  <SeriesTag key={seriesId}>
                    {getSeriesName(seriesId)}
                    <RemoveTagButton
                      type="button"
                      onClick={() => handleSeriesRemove(seriesId)}
                      aria-label="Remove series"
                    >
                      <XIcon />
                    </RemoveTagButton>
                  </SeriesTag>
                ))}
              </SeriesTagsContainer>
            )}
          </div>
        </FormRow>

        <FieldWrapper noMargin>
          <Textarea
            label="About Event"
            value={settings.about || ''}
            onChange={handleChange('about')}
            placeholder="Enter event description..."
            rows={4}
          />
        </FieldWrapper>
      </Card>

      {/* Ticket Settings Card */}
      <Card>
        <CardHeader>
          <CardIcon>
            <TicketIcon />
          </CardIcon>
          <CardTitleGroup>
            <CardTitle>Ticket Settings</CardTitle>
            <CardSubtitle>
              Configure reserved seating and promotional pricing options
            </CardSubtitle>
          </CardTitleGroup>
        </CardHeader>

        <FormRow columns="1fr 1fr">
          <ToggleRow>
            <ToggleInfo>
              <ToggleIcon>
                <ReservedSeatIcon />
              </ToggleIcon>
              <ToggleText>
                <ToggleLabel>Reserved Seating</ToggleLabel>
                <ToggleDescription>
                  Let customers choose specific seats, camping spots, or parking
                  spaces from an interactive map
                </ToggleDescription>
              </ToggleText>
            </ToggleInfo>
            <Toggle>
              <ToggleInput
                type="checkbox"
                checked={!!settings.reserved_seats}
                onChange={handleToggleChange('reserved_seats')}
              />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>

          <ToggleRow>
            <ToggleInfo>
              <ToggleIcon>
                <SalesWindowIcon />
              </ToggleIcon>
              <ToggleText>
                <ToggleLabel>Sales Windows</ToggleLabel>
                <ToggleDescription>
                  Create Early Bird, Last Chance, or other time-based pricing
                  tiers for your tickets
                </ToggleDescription>
              </ToggleText>
            </ToggleInfo>
            <Toggle>
              <ToggleInput
                type="checkbox"
                checked={!!settings.sales_windows}
                onChange={handleToggleChange('sales_windows')}
              />
              <ToggleSlider />
            </Toggle>
          </ToggleRow>
        </FormRow>

        {settings.reserved_seats && (
          <ExpandedSection>
            <InfoBox>
              <InfoText>
                You can set a default seating chart here for all reserved
                tickets, or leave it empty and assign different charts to
                individual tickets later. This is useful if you have multiple
                seating areas (e.g., grandstand seats vs. camping spots) that
                use different charts.
              </InfoText>
            </InfoBox>

            <SectionLabel>
              Event Seating Chart
              <OptionalBadge>(Optional)</OptionalBadge>
            </SectionLabel>
            <SearchableSelect
              value={settings.seating_chart_id || null}
              onChange={handleSeatingChartChange}
              options={seatingChartOptions}
              loading={chartsLoading}
              placeholder="Search seating charts..."
              isClearable
            />
          </ExpandedSection>
        )}
      </Card>

      {/* Event Type & Divisions Card */}
      <Card>
        <CardHeader>
          <CardIcon>
            <LayersIcon />
          </CardIcon>
          <CardTitleGroup>
            <CardTitle>Event Type & Divisions</CardTitle>
            <CardSubtitle>
              Categorize your event and select racing divisions
            </CardSubtitle>
          </CardTitleGroup>
        </CardHeader>

        <FormRow columns="1fr 1fr">
          {/* Event Types */}
          <div>
            <SearchableSelect
              key={`event-type-select-${(settings.event_types || []).length}`}
              label="Event Types"
              placeholder="Search and add event types..."
              options={EVENT_TYPE_OPTIONS.filter(
                (t) => !(settings.event_types || []).includes(t.value)
              )}
              onChange={handleEventTypeAdd}
              value={null}
            />
            {(settings.event_types || []).length > 0 && (
              <SeriesTagsContainer>
                {(settings.event_types || []).map((eventType) => (
                  <SeriesTag key={eventType}>
                    {getEventTypeLabel(eventType)}
                    <RemoveTagButton
                      type="button"
                      onClick={() => handleEventTypeRemove(eventType)}
                      aria-label="Remove event type"
                    >
                      <XIcon />
                    </RemoveTagButton>
                  </SeriesTag>
                ))}
              </SeriesTagsContainer>
            )}
          </div>

          {/* Divisions */}
          <div>
            {divisions.length === 0 ? (
              <>
                <SectionTitle style={{ marginBottom: 8 }}>
                  Racing Divisions
                </SectionTitle>
                <EmptyState>
                  No divisions available.
                  <br />
                  Divisions are configured in the account settings.
                </EmptyState>
              </>
            ) : (
              <>
                <SearchableSelect
                  key={`division-select-${
                    (settings.division_ids || []).length
                  }`}
                  label="Racing Divisions"
                  placeholder="Search and add divisions..."
                  options={divisionOptions.filter(
                    (d) => !(settings.division_ids || []).includes(d.value)
                  )}
                  onChange={handleDivisionAdd}
                  value={null}
                />
                {(settings.division_ids || []).length > 0 && (
                  <SeriesTagsContainer>
                    {(settings.division_ids || []).map((divisionId) => (
                      <SeriesTag key={divisionId}>
                        {getDivisionName(divisionId)}
                        <RemoveTagButton
                          type="button"
                          onClick={() => handleDivisionRemove(divisionId)}
                          aria-label="Remove division"
                        >
                          <XIcon />
                        </RemoveTagButton>
                      </SeriesTag>
                    ))}
                  </SeriesTagsContainer>
                )}
              </>
            )}
          </div>
        </FormRow>
      </Card>

      {/* Admin Settings Card (Collapsible) */}
      <Card>
        <CollapsibleHeader
          type="button"
          onClick={() => setAdminSettingsOpen(!adminSettingsOpen)}
        >
          <CardHeader style={{ marginBottom: 0 }}>
            <CardIcon>
              <SettingsIcon />
            </CardIcon>
            <CardTitleGroup>
              <CardTitle>Advanced Settings</CardTitle>
              <CardSubtitle>
                Optional configuration for capacity, access, and more
              </CardSubtitle>
            </CardTitleGroup>
          </CardHeader>
          <CollapsibleIcon isOpen={adminSettingsOpen}>
            <ChevronDownIcon />
          </CollapsibleIcon>
        </CollapsibleHeader>

        <CollapsibleContent isOpen={adminSettingsOpen}>
          <FormRow columns="1fr 1fr">
            <FieldWrapper noMargin>
              <Input
                label="Event Capacity"
                type="number"
                value={settings.capacity || ''}
                onChange={handleChange('capacity')}
                placeholder="Leave blank for unlimited"
              />
            </FieldWrapper>
            <ToggleRow>
              <ToggleInfo>
                <ToggleIcon>
                  <SettingsIcon />
                </ToggleIcon>
                <ToggleText>
                  <ToggleLabel>Enable Scan Out</ToggleLabel>
                  <ToggleDescription>
                    Allow fans to scan in and out for re-entry
                  </ToggleDescription>
                </ToggleText>
              </ToggleInfo>
              <Toggle>
                <ToggleInput
                  type="checkbox"
                  checked={!!settings.admin_scan_out}
                  onChange={handleToggleChange('admin_scan_out')}
                />
                <ToggleSlider />
              </Toggle>
            </ToggleRow>
          </FormRow>

          <Divider />

          <FormRow>
            <FieldWrapper noMargin>
              <Input
                label="Website URL"
                type="url"
                value={settings.admin_website_url || ''}
                onChange={handleChange('admin_website_url')}
                placeholder="https://..."
              />
            </FieldWrapper>
            <FieldWrapper noMargin>
              <Input
                label="Schedule URL"
                type="url"
                value={settings.admin_schedule_url || ''}
                onChange={handleChange('admin_schedule_url')}
                placeholder="https://..."
              />
            </FieldWrapper>
          </FormRow>

          <Divider />

          <SectionTitle>Event Code</SectionTitle>
          <SectionDescription>
            Require fans to enter a code to view this event. Optionally set an
            expiration date and time for the code to automatically become
            inactive.
          </SectionDescription>

          <FormRow columns="1fr 1fr">
            <FieldWrapper noMargin>
              <Input
                label="Event Code"
                value={settings.code || ''}
                onChange={handleChange('code')}
                placeholder="Enter code"
              />
            </FieldWrapper>
            <FieldWrapper noMargin>
              <FieldLabel>Set Expiration (Optional)</FieldLabel>
              <DateTimePicker
                value={getCodeExpirationValue()}
                onChange={handleCodeExpirationChange}
                showTime
              />
            </FieldWrapper>
          </FormRow>
        </CollapsibleContent>
      </Card>
    </Container>
  );
};

DetailsTab.propTypes = {
  settings: PropTypes.shape({
    name: PropTypes.string,
    event_types: PropTypes.arrayOf(PropTypes.string),
    about: PropTypes.string,
    capacity: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    track_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    division_ids: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    series_ids: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    ),
    reserved_seats: PropTypes.bool,
    sales_windows: PropTypes.bool,
    seating_chart_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    admin_scan_out: PropTypes.bool,
    admin_website_url: PropTypes.string,
    admin_schedule_url: PropTypes.string,
    code: PropTypes.string,
    code_date: PropTypes.string,
    code_time: PropTypes.string,
  }),
  onSettingsChange: PropTypes.func.isRequired,
  divisions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  loading: PropTypes.bool,
};

export default DetailsTab;
